import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllMetrics, getAllMetricsCacheKey } from 'features/metrics/metricsActions';
import { buildMetricsFilters, getFilterHash } from 'features/metrics/metricsActions';

/**
 * Custom hook that fetches all Performance Metrics values in a single VDS call.
 *
 * All 8 metric cards share one cache entry keyed by
 * {@code ${tenantCd}_${filterHash}}.
 *
 * @returns {{ data: Object|null, loading: boolean, error: string|null }}
 *   {@code data} is a flat object keyed by VDS field alias, e.g.
 *   {@code { "Metric - Total Claims PEPM % Diff": -54.85, ... }}
 */
export const useAllMetrics = () => {
  const dispatch = useDispatch();
  const tenantCd = useSelector((state) => state.user.currentTenantCd);
  const appliedFilters = useSelector((state) => state.globalFilters?.applied);
  const defaultsSeeded = useSelector((state) => state.globalFilters?.defaultsSeeded);

  const filterHash = useMemo(
    () => getFilterHash(buildMetricsFilters(appliedFilters)),
    [appliedFilters]
  );

  const cacheKey = tenantCd ? getAllMetricsCacheKey(tenantCd, filterHash) : null;

  const cached = useSelector((state) =>
    cacheKey ? state.metrics.cache[cacheKey] : null
  );

  const hasCachedData = Boolean(
    cached?.data &&
    typeof cached.data === 'object' &&
    !Array.isArray(cached.data) &&
    Object.keys(cached.data).length > 0
  );
  const isWaitingForSeed = Boolean(tenantCd) && !defaultsSeeded;
  const [localLoading, setLocalLoading] = useState(!tenantCd || !hasCachedData);

  useEffect(() => {
    if (!tenantCd || !defaultsSeeded) return;

    let mounted = true;

    if (hasCachedData) {
      setLocalLoading(false);
      return;
    }

    setLocalLoading(true);
    dispatch(fetchAllMetrics())
      .catch(() => {})
      .finally(() => { if (mounted) setLocalLoading(false); });

    return () => { mounted = false; };
  }, [dispatch, tenantCd, filterHash, defaultsSeeded, cacheKey, hasCachedData]);

  return {
    data:    cached?.data  || null,
    loading: isWaitingForSeed || localLoading || cached?.loading || false,
    error:   cached?.error || null
  };
};
